import {wordIdToFilename} from '$lib/helper-fns/wordIdToFilename';
import {Word} from '$lib/classes/data/word';
import {Sentence} from '$lib/classes/data/sentence';
import type {WordId, SentenceId} from '$lib/classes/data/_primitives';
import {PlaybackRate} from '$lib/classes/_types/playback-rate';

export class AudioPlayer {

    // TODO make private
    playbackRateMap: Record<PlaybackRate, number> = {
        [PlaybackRate.Slower]: 0.7,
        [PlaybackRate.Slow]: 0.85,
        [PlaybackRate.Normal]: 1.0,
    };

    // workaround for SSR >> prevent setting/checking null
    private audio: HTMLAudioElement = {
        pause: () => {}, play: () => Promise.resolve(), src: '', currentTime: 0
    } as HTMLAudioElement;

    private playPromise = Promise.resolve()
    private currentToken: number = 0 // token for every action to only handle the latest action after having awaited play promise

    private isPlaying = $state(false)
    private currentlyPlaying: Word | Sentence | null = $state(null)
    private indicateIsPlaying = true // don't indicate playing solution audio in learn session

    private loadedAudios: Map<WordId | SentenceId, HTMLAudioElement> = new Map();

    constructor(
        private readonly ASSETS_HOST: string
    ) {}

    private getAudio(item: Word | Sentence) {
        const audio = new Audio(this.getSrc(item))
        audio.addEventListener('playing', () => {
            this.isPlaying = true
        })
        audio.addEventListener('pause', () => {
            this.isPlaying = false
            this.currentlyPlaying = null
        })
        audio.addEventListener('ended', () => {
            this.isPlaying = false
            this.currentlyPlaying = null
        })
        return audio
    }

    private getSrc(item: Word | Sentence) {
        if (item instanceof Word) {
            return `${this.ASSETS_HOST}/audio/words/${wordIdToFilename(item.id)}.mp3`
        } else {
            return `${this.ASSETS_HOST}/audio/sentences/${item.id}.mp3`
        }
    }

    load(item: Word | Sentence): HTMLAudioElement {
        if (this.loadedAudios.has(item.id)) {
            return this.loadedAudios.get(item.id)!
        } else {
            const audio = this.getAudio(item)
            this.loadedAudios.set(item.id, audio)
            return audio
        }
    }

    async play(
        item: Word | Sentence,
        options: {
            indicateIsPlaying?: boolean,
            playbackRate?: PlaybackRate,
        } = {}
    ) {
        const {
            indicateIsPlaying = true,
            playbackRate = PlaybackRate.Normal,
        } = options;

        const thisToken = ++this.currentToken
        await this.playPromise
        if (thisToken !== this.currentToken) return

        this.indicateIsPlaying = indicateIsPlaying
        this.audio.pause()

        //timeout needed so that the 'pause' event callback is executed >> change of this.isPlaying is registered >> isPlayingItem correctly indicated
        setTimeout(() => {
            if (this.currentlyPlaying !== item) {
                this.currentlyPlaying = item
                this.audio = this.load(item)
            }
            this.audio.currentTime = 0
            this.audio.playbackRate = this.playbackRateMap[playbackRate]
            this.playPromise = this.audio.play().catch(error => {
                console.error('ERROR AudioPlayer.play', item.id, this.audio.src, error)
            })
        }, 0)
    }

    async pause() {
        const thisToken = ++this.currentToken
        await this.playPromise
        if (thisToken !== this.currentToken) return

        this.audio.pause()
    }

    isPlayingItem(item: Word | Sentence) {
        return this.isPlaying && this.currentlyPlaying === item && this.indicateIsPlaying
    }
}